/**
 * Error Boundary Component
 *
 * Catches JavaScript errors in child component tree and displays
 * a fallback UI instead of crashing the whole app.
 *
 * @version 1.0.0
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './Button';

export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode;
  /** Custom fallback component */
  fallback?: ReactNode;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show technical details (dev mode) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary class component
 *
 * Must be a class component because error boundaries require
 * getDerivedStateFromError and componentDidCatch lifecycle methods.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by ErrorBoundary:', error);
      console.error('Component stack:', errorInfo.componentStack);
    }

    // In production, you might want to send to error tracking service
    // Example: Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, showDetails } = this.props;

    if (hasError) {
      // Return custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div
          className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
          role="alert"
          aria-live="assertive"
        >
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-error" aria-hidden="true" />
            </div>

            {/* Error Message */}
            <h1 className="text-h3 text-warm-gray-dark mb-2">
              Something went wrong
            </h1>
            <p className="text-body text-warm-gray mb-6">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                variant="primary"
                onClick={this.handleReset}
                leftIcon={<RefreshCw className="w-4 h-4" />}
              >
                Try Again
              </Button>
              <Button
                variant="secondary"
                onClick={this.handleGoHome}
                leftIcon={<Home className="w-4 h-4" />}
              >
                Go Home
              </Button>
            </div>

            {/* Technical Details (Development Only) */}
            {(showDetails || import.meta.env.DEV) && error && (
              <details className="text-left bg-gray-50 rounded-lg p-4 mt-4">
                <summary className="text-body-sm font-medium text-warm-gray cursor-pointer">
                  Technical Details
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <p className="text-caption font-medium text-warm-gray">Error:</p>
                    <pre className="text-caption text-error overflow-x-auto whitespace-pre-wrap break-words">
                      {error.message}
                    </pre>
                  </div>
                  {errorInfo && (
                    <div>
                      <p className="text-caption font-medium text-warm-gray">Component Stack:</p>
                      <pre className="text-caption text-warm-gray-light overflow-x-auto whitespace-pre-wrap break-words max-h-40">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}

            {/* Contact Support Link */}
            <p className="text-body-sm text-warm-gray-light mt-4">
              If the problem persists, please{' '}
              <a
                href="/contact"
                className="text-primary-blue hover:text-primary-blue-dark underline"
              >
                contact support
              </a>
              .
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

/**
 * Hook to programmatically trigger error boundary
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const throwError = useErrorBoundary();
 *
 *   const handleError = () => {
 *     throwError(new Error('Something went wrong'));
 *   };
 *
 *   return <button onClick={handleError}>Trigger Error</button>;
 * }
 * ```
 */
export function useErrorBoundary(): (error: Error) => void {
  return (error: Error) => {
    throw error;
  };
}

/**
 * Simple error fallback component for less critical errors
 */
export interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  message?: string;
}

export function ErrorFallback({
  error,
  resetError,
  message = 'Something went wrong loading this content.',
}: ErrorFallbackProps): JSX.Element {
  return (
    <div
      className="bg-error/5 border border-error/20 rounded-lg p-4"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-body-sm text-warm-gray-dark">{message}</p>
          {import.meta.env.DEV && error && (
            <p className="text-caption text-error mt-1">{error.message}</p>
          )}
          {resetError && (
            <button
              onClick={resetError}
              className="text-body-sm text-primary-blue hover:text-primary-blue-dark mt-2 underline"
            >
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
